import './deal-builder.css';

import { EventEmitter } from "eventemitter3";
import { Util } from '../../../utils/util';
import { CouponTypeList, ItemStyle, Modals, OrderType } from '../../../types/enums';
import { Modal } from '../modal';
import { Order } from '../../../models/order';
import { Data } from '../../../models/data';
import { OnlineOrderingUtil } from '../../../online-ordering/online-ordering-util';
import { ItemPricingUnavailableError } from '../../../online-ordering/errors/item-pricing-unavailable-error';

import { ILoyaltyProvider } from '../../../services/loyalty-provider/loyalty-provider.interface';
import { DataLayerService } from '../../../services/data-layer-service';
import { ProfileService } from '../../../services/profile-service';

import { MealDealItemSectionCard } from '../../shared/MealDealItemSectionCard/meal-deal-item-section-card';
import { TimedMessage } from '../../shared/TimedMessage/timed-message';

import { IDealBuilder, IItemsByCategory, IDealBuilderCouponDTO, IDealBuilderItem } from './deal-builder.interface';
import { IDataItem, IDataModifierDisplayGroup } from '../../../models/data.interface';
import { IOrderItem, IIncompleteDiscount, IOrderCoupon, IOrderItemCoupon } from "../../../models/order.interface";
import { IApplyCouponByIdParamsPartial } from '../../../models/coupons.interface';
import { IUserLoyaltyReward } from '../../../models/user.interface';

import { EventTypes } from '../../../core/event-bus';
import { Context } from '../../../models/context';
import { LocalizationService } from '../../../services/localization-service';
import { ICustomizeItemProps } from '../CustomizeItem/customize-item.interface';
import { IPillBarData } from '../../shared/PillBar/pill-bar.interface';
import { PillBar } from '../../shared/PillBar/pill-bar';
import { IModDropdown } from '@ts/online-ordering/interfaces/online-ordering.interface';

export class DealBuilder extends Modal {
    private readonly _data: Data;
    private readonly _order: Order;
    private readonly _profileService: ProfileService;
    private readonly _loyaltyProvider: ILoyaltyProvider;
    private readonly _item: IDealBuilder;

    get selectedItemsCount(): number {
        return this._item.groupState.selectedItemsCount;
    }

    set selectedItemsCount(value: number) {
        this._item.groupState.selectedItemsCount = value;
        this._updateSelectedItemsLabel();
        this._renderProgressDivs();
    }

    get isMixAndMatch(): boolean {
        return this._item.coupon?.CouponTypeId == CouponTypeList.MIX_AND_MATCH_PRICE || this._item.coupon?.CouponTypeId == CouponTypeList.MIX_AND_MATCH_PERCENTAGE;
    }

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, order: Order, dataLayer: DataLayerService, loyaltyProvider: ILoyaltyProvider, profileService: ProfileService) {
        super("modal_deal_builder", eventBus, context, dataLayer, localizationService, { hasCloseButton: true, ariaLabel: "Deal Builder" }, Modals.MEAL_DEAL);
        this._data = data;
        this._order = order;
        this._loyaltyProvider = loyaltyProvider;
        this._profileService = profileService;
        this._item = this._initialModel();
        this._eventBusListeners();
    }

    /** 
     * Initializes the DealBuilder class.
    */
    public override init = async (): Promise<void> => {
        super.init();
        await this._setupAsync();
    }

    /**
     * Checks if the meal deal is already complete, and if not, sets up the deal builder by populating and its data, rendering the page, and setting event listeners.
     */
    private async _setupAsync(): Promise<void> {
        const isComplete = await this._checkSingleGroupSingleItemNonCustomize();
        if (!isComplete) {
            const items = this._getQualifyingItemsFromCart();
            this._populateData(items);
            this._renderPage();
            this._setEventListeners();
        }
    }

    /**
     * Sets up event listeners for group header accordion, category selection, add deal to the order, and modal delete button
     */
    protected readonly _setEventListeners = (): void => {
        const groupHeaders = Array.from(document.getElementsByName("deal-builder-group-header"));
        for (const header of groupHeaders) {
            header.onclick = () => {
                this._toggleAccordion(header);
            }
        }

        document.getElementById("btn_deal_builder_add_to_order")?.addEventListener("click", this._onClickAddDealToOrder);
        document.getElementById("modal_deal_builder_delete")?.addEventListener("click", this._onClickDealBuilderClose);
    }

    /**
     * Creates the deal builder model
     * @param coupon - The coupon associated with the deal
     * @param isCampaignCode - Whether the coupon is a campaign code
     * @param isPromotionCode - Whether the coupon is a promotion code
     * @param isBankedCurrency - Whether the coupon is banked currency
     * @param rewardId - The reward ID associated with the coupon if applicable
     * @param orderTypeId - The order type ID of the order
     * @param groupState - Holds state information of all groups
     * @returns
     */
    private readonly _initialModel = (): IDealBuilder => {
        return {
            coupon: null,
            isCampaignCode: false,
            isPromotionCode: false,
            isBankedCurrency: false,
            offerCode: null,
            rewardId: null,
            sourceId: null,
            channelId: null,
            orderTypeId: this._context.getProperty("defaultOrderType"),
            groupState: { requiredItems: 0, totalGroups: 0, selectedGroup: 0, itemStates: [], categoryStates: [], selectedItemsCount: 0 },
            isSpecialCode: false,
            isSingleUseCoupon: false
        };
    }

    /**
     * Clears the model to default values
     */
    private readonly _clearModel = (): void => {
        this._item.coupon = null;
        this._item.isCampaignCode = false;
        this._item.isPromotionCode = false;
        this._item.isBankedCurrency = false;
        this._item.offerCode = null;
        this._item.rewardId = null;
        this._item.sourceId = null;
        this._item.channelId = null;
        this._item.orderTypeId = this._context.getProperty("defaultOrderType");
        this._item.groupState = this._item.groupState = { requiredItems: 0, totalGroups: 0, selectedGroup: 0, itemStates: [], categoryStates: [], selectedItemsCount: 0 };
        this._item.isSpecialCode = false;
        this._item.isSingleUseCoupon = false;
    }

    /**
     * Sets event bus listeners when order types have changed and when a meal deal is opened
     */
    private readonly _eventBusListeners = () => {
        this._eventBus.on(EventTypes.MEAL_DEAL_OPEN, async (couponDto: IDealBuilderCouponDTO) => {
            if (couponDto && couponDto.coupon && couponDto.coupon.CouponId > 0) {
                this._item.coupon = couponDto.coupon;
                this._item.isCampaignCode = couponDto.isCampaignCode;
                this._item.isPromotionCode = couponDto.isPromotionCode;
                this._item.rewardId = couponDto.rewardId;
                this._item.offerCode = couponDto.offerCode;
                this._item.sourceId = couponDto.sourceId;
                this._item.channelId = couponDto.channelId;
                this._item.isSpecialCode = couponDto.isSpecialCode;
                this._item.isSingleUseCoupon = couponDto.isSingleUseCoupon;
                await this._setupAsync();
                this.open();
                this._renderContentHeight(); // BUG:63303 - this has to be calculated after opening modal or else height measurements will be 0 
            }
        });
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._orderTypeChanged);
    }

    private readonly _orderTypeChanged = (orderTypeId: OrderType): void => {
        this._item.orderTypeId = orderTypeId;
    }

    /**
     * Gets any existing qualifying items from the order
     * @returns {IOrderItem[]} - Returns an array of qualifying items}
     */
    private _getQualifyingItemsFromCart(): IOrderItem[] {
        if (!this._item.coupon)
            return [];

        const qualifyingItems = this._order.getCouponQualifyingItems(this._item.coupon);
        const qualifyingKeys = qualifyingItems ? qualifyingItems.map(x => x.ItemKey) : [];
        return this._order.getProperty("Items").filter(x => qualifyingKeys.includes(x.ItemKey));
    }

    /**
     * Checks if the coupon is a single group, single item, and non-customizable deal, 
     * then attempts to apply the deal if the coupon is none of these
     * @returns {boolean} - Returns true if the deal is complete, false otherwise
     */
    private readonly _checkSingleGroupSingleItemNonCustomize = async (): Promise<boolean> => {
        if (this._item.coupon?.Items && this._item.coupon?.Items.length == 1) {
            const couponItem = this._item.coupon.Items[0];
            const mItem = this._data.getProperty("Items")?.find(x => x.ItemId == couponItem.ItemId);
            if (!mItem)
                return false;

            const sizes = this._getCouponItemSizesByGroup(couponItem.ItemId, couponItem.GroupIndex);
            const mods = this._createModifierDropDown(mItem, this._data, this._context);
            const [mustCustomize, canCustomize] = this._getCustomizeRules(mItem);

            if (mItem && !(mustCustomize || canCustomize || mods.DropdownIds || sizes.length > 1)) {
                //2DO: Check if meal deal was initialized by couponCode before applying deal
                await this._tryApplyDealAsync(true);
                await this._closeDealBuilderAsync();
                return true;
            }
        }

        return false;
    }

    private _createModifierDropDown(mItem: IDataItem, data: Data, context: Context): IModDropdown {
        if (mItem.ModifierDisplayGroups.length === 0)
            return { element: null, DropdownIds: null };

        const ddlIds: string[] = [];

        const div = document.createElement("div");
        for (const [index, modG] of mItem.ModifierDisplayGroups.entries()) {
            const mdg = data.getProperty("ModifierDisplayGroups").find(mdg => modG.ModifierDisplayGroupId === mdg.ModifierDisplayGroupId);
            let html = "";

            if (!mdg?.IsVisible)
                return { element: null, DropdownIds: null };

            const selId = `sel_item_mod_dg_${mItem.ItemId}_${mdg.ModifierDisplayGroupId}`;
            ddlIds.push(selId);

            const optionsHTML = modG.Modifiers.map((mod, index2) => {
                return `<option value="${mod.ModifierId}" data-mod-iidx="${index2}" data-mdg-iidx="${index}" ltagj="${Util.toLtagj(Array.from(data.getProperty("Modifiers"))[mod.Index]?.Names)}" ${mod.IsDefault ? "selected" : ""}>
                    ${this._localizationService.GetName(Array.from(data.getProperty("Modifiers"))[mod.Index].Names)}
                </option>`;
            }).join('');

            html += `
                <div class="m1-lr">
                    <label class="m1-b" for="${selId}" ltagj="${Util.toLtagj(mdg.Names)}">${this._localizationService.GetName(mdg.Names)}</label>
                    <select id="${selId}" class="m1-b">
                        ${optionsHTML}
                    </select>
                </div>`;

            div.innerHTML += html;
        }

        return { element: div, DropdownIds: ddlIds };
    }

    /**
     * Populate the deal builder model with the required data if coupon exists
     */
    private readonly _populateData = (qualifiedItems: IOrderItem[]): void => {
        if (this._item.coupon) {
            this._getItemGroups(qualifiedItems);
        }
    }

    /**
     * Gets a list of all of an item's sizes by its item ID and group number
     * @param itemId - ID of the item
     * @param groupIndex - Group number
     * @returns {number[]} - Array of size IDs
     */
    private readonly _getCouponItemSizesByGroup = (itemId: number, groupIndex: number): number[] => {
        if (this._item.coupon?.Items) {
            const items = this._item.coupon?.Items.filter(x => x.GroupIndex == groupIndex && x.ItemId == itemId);
            return (items.length > 0) ? items.map(x => x.SizeId) : [];
        }
        return [];
    }

    /**
     * Gets the customize rules for an item
     * @param mItem - The item to check
     * @returns {[boolean, boolean]} - Returns a tuple of two booleans: the first indicates if the item must be customized, the second indicates if the item can be customized
     */
    private readonly _getCustomizeRules = (mItem: IDataItem): [boolean, boolean] => {
        const mustCustomize = this._mustCustomize(mItem, this._data.getProperty("ModifierDisplayGroups"), ItemStyle.VERTICAL_SIZE_BTN);
        const canCustomize = this._canCustomize(mItem, this._data.getProperty("ModifierDisplayGroups"));
        return [mustCustomize, canCustomize];
    }

    private _mustCustomize(mItem: IDataItem, modDisplayGroup: IDataModifierDisplayGroup[], itemStyleID: number): boolean {
        if (!mItem.ModifierDisplayGroups) return false;

        let mustSelectDG_WithNoDefault = false;
        let multiSelectDG = false;

        for (const iMdg of mItem.ModifierDisplayGroups) {
            const mdg = modDisplayGroup[iMdg.Index];
            const defCount = iMdg.Modifiers.filter(mod => mod.IsDefault).length;

            if (itemStyleID === ItemStyle.VERTICAL_SIZE_BTN && mdg.MaxSelectionCount > 1 && !mdg.MustToggle) {
                return true;
            }

            const [mSelectDgNoDef, mSelectDg] = this._checkModifierGroupFlags(defCount, mdg);
            if (mSelectDgNoDef)
                mustSelectDG_WithNoDefault = true;
            if (mSelectDg)
                multiSelectDG = true

            if (mdg.MaxSelectionCount !== 1 && !mdg.MustToggle && defCount < mdg.MinSelectionCount) {
                /*
                * if default mod counts is less than
                * min mod in group
                * item must be customized
                */
                return true;
            }
        }

        if (mustSelectDG_WithNoDefault && multiSelectDG)
            return true;

        return false;
    }

    private _checkModifierGroupFlags(defCount: number, mdg: IDataModifierDisplayGroup): [boolean, boolean] {
        return [
            defCount === 0 && mdg.MinSelectionCount > 0,
            mdg.MaxSelectionCount > 1 && !mdg.MustToggle
        ];
    }

    private _canCustomize(mItem: IDataItem, modDisplayGroup: IDataModifierDisplayGroup[]): boolean {
        return mItem.ModifierDisplayGroups.some(mdg => {
            const { MaxSelectionCount, MustToggle } = modDisplayGroup[mdg.Index];
            return MaxSelectionCount > 1 && !MustToggle;
        }) ?? false;
    }

    /**
     * Populates the group state model with the number of groups and any existing qualified items already applied to the order
     * If Mix and Match, populates the group state model instead with the number of minimum number quantity required and any existing qualified items already applied to the order
     * @param qualifiedItems - The list of qualified items to populate the model with
     */
    private readonly _getItemGroups = (qualifiedItems: IOrderItem[]): void => {
        this._item.groupState.requiredItems = this._item.groupState.totalGroups = this._getRequiredItemsCount();
        if (this.isMixAndMatch) {
            this._getItemsForMixAndMatch(qualifiedItems);
        } else {
            for (let i = 1; i <= this._item.groupState.requiredItems; i++) {
                const items = this._getQualifiedItems(i, qualifiedItems);
                if (items.length > 0) {
                    const itemKey = items[0].ItemKey;
                    this._item.groupState.itemStates.push({ groupIndex: i, itemKey: itemKey, items: items, required: true });
                } else {
                    this._item.groupState.itemStates.push({ groupIndex: i, itemKey: -1, items: [], required: true });
                }
            }
        }
    }

    /**
     * Gets items from group 1 specifically for Mix and Match coupons
     * @param qualifiedItems
     */
    private readonly _getItemsForMixAndMatch = (qualifiedItems: IOrderItem[]): void => {
        const items = this._getQualifiedItems(1, qualifiedItems);
        for (let i = 1; i <= this._item.groupState.requiredItems; i++) {
            const itemsIndex = i - 1;
            if (items.length > 0 && items[itemsIndex] !== undefined) {
                const itemKey = items[itemsIndex].ItemKey;
                this._item.groupState.itemStates.push({ groupIndex: i, itemKey: itemKey, items: [items[itemsIndex]], required: true });
            } else {
                this._item.groupState.itemStates.push({ groupIndex: i, itemKey: -1, items: [], required: true });
            }
        }
    }

    /**
     * Gets the number of required items needed to complete the deal depending on the coupon type
     * @returns {number} - Returns the number of required items for completing the deal
     */
    private readonly _getRequiredItemsCount = (): number => {
        if (this.isMixAndMatch)
            return this._item.coupon?.MinimumItemQuantity || 0;
        else
            return this._item.coupon?.Items ? Array.from(new Set<(number | undefined)>(this._item.coupon?.Items.map(cpn => cpn.GroupIndex))).filter(Boolean).length : 0;
    }

    /**
     * Gets the qualified items for a specific group
     * @param groupIndex - The group number
     * @param qualifiedItems - The list of qualified items to filter from
     * @returns {IOrderItem[]} - Returns an array of qualified items
     */
    private _getQualifiedItems (groupIndex: number, qualifiedItems: IOrderItem[]): IOrderItem[] {
        let items: IOrderItem[] = [];
        items = this._getQualifiedItemsForGroup(groupIndex, qualifiedItems);
        return items;
    }

    /**
     * Gets the qualified items for a specific group
     * @param groupIndex - The group number
     * @param qualifiedItems - The list of qualified items to filter from
     * @returns {IOrderItem[]} - Returns an array of qualified items for the group
     */
    private _getQualifiedItemsForGroup(groupIndex: number, qualifiedItems: IOrderItem[]): IOrderItem[] {
        const items: IOrderItem[] = [];
        const groupItems = this._item.coupon?.Items.filter(x => x.GroupIndex == groupIndex);
        if (groupItems) {
            for (const qItem of qualifiedItems) {
                const item = groupItems.find(x => x.ItemId == qItem.ItemId && x.SizeId == qItem.SizeId);
                if (item) {
                    items.push(qItem);
                    qualifiedItems.splice(qualifiedItems.indexOf(qItem), 1);

                    // Search for next half and add it
                    const nextHalfItem = qualifiedItems
                        .find(x => x.ItemKey == qItem.ItemKey
                            && x.ItemId == qItem.ItemId
                            && x.SizeId == qItem.SizeId
                        );

                    if (nextHalfItem) {
                        items.push(nextHalfItem);
                        qualifiedItems.splice(qualifiedItems.indexOf(nextHalfItem), 1);
                    }
                    break;
                }
            }
        }
        return items;
    }

    /**
     * Updates the selected items label based on the coupon type
    */
    private readonly _updateSelectedItemsLabel = (): void => {
        if (this.isMixAndMatch)
            Util.setElement("innerHTML", "div_deal_selected_status", "");
        else
            Util.setElement("innerHTML", "div_deal_selected_status", `<b>${this._item.groupState.selectedItemsCount} of ${this._item.groupState.requiredItems}</b> Items Selected`);
            
    }

    /**
     * Updates the text and style of the add to order button based on the selected items count
     */
    private readonly _updateAddToOrderBtn = async (): Promise<void> => {
        const addOrderButton = document.getElementById("btn_deal_builder_add_to_order") as HTMLButtonElement;

        if (this.selectedItemsCount >= this._item.groupState.requiredItems) {
            addOrderButton.disabled = false;

            Util.setElement(
                "innerHTML",
                "spn_deal_builder_add_to_order",
                this._localizationService.names("AddDealToOrder"),
            );

            if (this._item.coupon) {
                const couponDiscount = await this._order.getCouponMealDealPrice(this._item.coupon);
                const country = this._data.getProperty("StoreInfo").country;
                const dealPriceText = `${Util.formatMoney(couponDiscount, country)}`;
                Util.setElement("innerHTML", "spn_deal_builder_price", dealPriceText);
            } else {
                Util.setElement("innerHTML", "spn_deal_builder_price", "");
            }

            Util.hideElement("spn_deal_builder_select_items");
            Util.showElement("spn_deal_builder_add_to_order");
            Util.showElement("spn_deal_builder_price");
        } else {
            addOrderButton.disabled = true;

            const btnText =
                this._item.groupState.requiredItems > 1
                    ? this._localizationService
                          .names("SelectItemsToAddtoOrder_Plural")
                          .replace("{{number}}", this._item.groupState.requiredItems.toString())
                    : this._localizationService.names("SelectItemsToAddtoOrder_Single");

            Util.setElement("innerHTML", "spn_deal_builder_select_items", btnText);

            Util.hideElement("spn_deal_builder_add_to_order");
            Util.hideElement("spn_deal_builder_price");
            Util.showElement("spn_deal_builder_select_items");
        }
    }

    /**
     * Renders the deal builder modal
     * Displays coupon image and information and creates the required group/item sections
     */
    private readonly _renderPage = (): void => {
        if (this._item.coupon) {
            if (this._elementDelete)
                this._elementDelete.style.display = "unset";

            const divContent = document.getElementById("div_deal_builder_content") as HTMLDivElement;
            divContent.innerHTML = "";
            divContent.style.height = "";
            divContent.style.minHeight = "100vh";
            this.selectedItemsCount = 0;

            this._renderCouponImage(divContent);
            this._renderCouponDetails(divContent);
            const divGroupItemSections = this._renderGroupItemSections();
            if(divGroupItemSections)
                divContent.appendChild(divGroupItemSections);

            this._renderExistingItems();

            Util.setElement("innerHTML", "spn_deal_builder_button_description", this._localizationService.names("DealBuilderButtonDescription"));
            this._updateAddToOrderBtn();
        }

        this._scrollToTop();
    }

    /**
     * Renders the coupon image for the deal builder
     * @param parentDiv -  The parent div to attach to
     */
    private _renderCouponImage(parentDiv: HTMLDivElement): void {
        const div = document.createElement("div");
        div.id = "div_deal_builder_image";
        div.classList.add("deal-builder-image");

        const img = document.createElement("img");
        img.id = "img_deal_builder_image";
        img.src = this._item.coupon?.Images?.Full ?? "";

        div.appendChild(img);
        parentDiv.appendChild(div);
    }

    /**
     * Renders the coupon details for the deal builder
     * @param parentDiv - The parent div to attach to
     */
    private _renderCouponDetails(parentDiv: HTMLDivElement): void {
        const div = document.createElement("div");
        div.classList.add("deal-builder-details");

        const h2 = document.createElement("h2");
        h2.id = "h2_deal_builder_name";
        h2.innerHTML = this._localizationService.GetName(this._item.coupon?.Names ?? []);

        const p = document.createElement("p");
        p.id = "p_deal_builder_description";
        p.classList.add("deal-builder-details-description");
        p.innerHTML = this._localizationService.GetDescription(this._item.coupon?.Descriptions ?? []);

        div.appendChild(h2);
        div.appendChild(p);
        parentDiv.appendChild(div);
    }

    /**
     * Renders the group item sections for the deal builder
     * @returns {HTMLDivElement} - Returns the either already existing or newly created group item sections element
     */
    private _renderGroupItemSections(): HTMLDivElement {
        const mainBodyId = "div_deal_builder_main_body";
        let divGroupItemSections = document.getElementById(mainBodyId) as HTMLDivElement;
        if(!divGroupItemSections){
            divGroupItemSections = document.createElement("div");
            divGroupItemSections.id = mainBodyId
        }
        divGroupItemSections.innerHTML = "";
        const maxGroupNum = Math.max(this._item.groupState.requiredItems, this._item.groupState.itemStates.length);
        for (let i = 1; i <= maxGroupNum; i++) {
            if (this.isMixAndMatch && i > this._item.groupState.itemStates.filter(x => x.itemKey != -1).length + 1)
                continue;

            const existingItemIsRequired = this._item.groupState.itemStates.find(x => x.groupIndex === i)?.required ?? true;
            this._createItemSection(divGroupItemSections, i, existingItemIsRequired);
        }
        return divGroupItemSections;
    }

    /**
     * Renders existing items for applicable Group Item Sections
     */
    private _renderExistingItems(): void {
        const maxGroupNum = Math.max(this._item.groupState.requiredItems, this._item.groupState.itemStates.length);
        for (let i = 1; i <= maxGroupNum; i++) {
            if (this.isMixAndMatch && i > this._item.groupState.itemStates.filter(x => x.itemKey != -1).length)
                continue;

            const existingItems = this._item.groupState.itemStates.find(x => x.groupIndex == i)?.items;
            if (existingItems && existingItems.length > 0) {
                this._updateGroupItemSection(i, existingItems[0].ItemId, true, existingItems[0].ItemKey);
            }
        }
    }

    /**
     * Renders the content height dynamically based on comparison to viewport
     */
    private _renderContentHeight(): void {
        const divTopBar = document.getElementById("div_deal_bulder_top_bar") as HTMLDivElement;
        const divContent = document.getElementById("div_deal_builder_content") as HTMLDivElement;
        const divBottomBar = document.getElementById("div_deal_builder_bottom_bar") as HTMLDivElement;
        if (divTopBar && divContent && divBottomBar) {
            const topBarHeight = divTopBar.offsetHeight;
            const divContentHeight = divContent.scrollHeight;
            const bottomBarHeight = divBottomBar.offsetHeight;
            if ((topBarHeight + divContentHeight + bottomBarHeight) > window.innerHeight) {
                divContent.style.height = `${divContentHeight - bottomBarHeight}px`;
                divContent.style.minHeight = "";
            }
        }
    }

    /**
     * Scrolls the customization content area to the top.
     */
    private _scrollToTop(): void {
        const div = document.getElementById("div_deal_builder_content");
        if (div)
            div.scrollTop = 0;
    }

    /**
     * Renders the progress bar divs to show the user's progress in selecting items
     */
    private readonly _renderProgressDivs = (): void => {
        const itemCount = this.isMixAndMatch ? this._item.groupState.totalGroups : this._item.groupState.requiredItems;
        const containerDiv = document.getElementById("div_deal_progress_bar");
        if (containerDiv) {
            containerDiv.innerHTML = '';

            for (let i = 0; i < itemCount; i++) {
                this._renderProgressDiv(i, itemCount, containerDiv);
            }
        }
    }

    /**
     * Renders a single div for the progress bar
     * @param i - The index of the div to render
     * @param itemCount - The total number of items that is possible to select for the deal
     * @param containerDiv - The parent div to append the progress bar div to
     */
    private readonly _renderProgressDiv = (i: number, itemCount: number, containerDiv: HTMLElement): void => {
        const div = document.createElement('div');
        div.id = `div_group_item_${i}`;
        const completeClass = this.selectedItemsCount > i ? "complete" : "incomplete";
        div.classList.add("deal-builder-progress-bar", completeClass);
        if (i == 0 && itemCount > 1) {
            div.classList.add("start")
        } else if (i == itemCount - 1 && itemCount > 1) {
            div.classList.add("end");
        } else if (i != 0 && i != itemCount - 1 && itemCount > 1) {
            div.classList.add("mid");
        } else {
            div.classList.add("single");
        }
        containerDiv.appendChild(div);
    }

    /**
     * Creates the item section for a group
     * @param parent - The parent element to append the item section to
     * @param groupIndex - The group number
     * @param isRequired - Whether the item is required for the deal
     */
    private readonly _createItemSection = (parent: HTMLElement, groupIndex: number, isRequired: boolean): void => {
        const divItemContainer = document.createElement("div");
        divItemContainer.id = `div_deal_builder_item_${groupIndex}`;

        const divItemHeader = this._createItemSectionHeader(groupIndex, isRequired);
        divItemContainer.appendChild(divItemHeader);

        const divItemBody = this._createItemSectionBody(groupIndex);
        divItemContainer.appendChild(divItemBody);
        parent.appendChild(divItemContainer);

        //render cateogry selection
        this._autoSelectItemCategory(groupIndex);
    }

    /**
     * Creates the item section header for a group
     * @param groupIndex - The group number
     * @param isRequired - Whether the item is required for the deal
     * @returns {HTMLElement} - Returns the item section header element
     */
    private readonly _createItemSectionHeader = (groupIndex: number, isRequired: boolean): HTMLElement => {
        const itemHeaderText = `${Util.GetOrdinalIndicator(groupIndex)} ${this._localizationService.names("Item")}`;
        const itemTagText = isRequired ? this._localizationService.names("Required") : this._localizationService.names("Optional");

        const html = `
            <div class="deal-builder-group-header" name="deal-builder-group-header">
                <div class="header-text">
                    <div class="accordion-icon" aria-hidden="true">
                        <i class="icon-arrow-back"></i>
                    </div>
                    <h3 id="h_deal_builder_group_${groupIndex}"><button type="button" id="btn_deal_builder_group_toggle_${groupIndex}" class="deal-builder-group-toggle" aria-expanded="true" aria-controls="div_deal_builder_item_body_${groupIndex}">${itemHeaderText}</button></h3>
                </div>
                <div>
                    <span id="spn_deal_builder_group_header_tag_${groupIndex}">${itemTagText}</span>
                    <span id="spn_deal_builder_group_header_tag_added_${groupIndex}" class="added-tag hidden">${this._localizationService.names("Added")}</span>
                </div>
            </div>
        `;

        const sectionHeader = Util.createHtmlElementFromTemplate(html);

        sectionHeader.onclick = () => {
            this._toggleAccordion(sectionHeader);
        }
        return sectionHeader;
    }

    /**
     * Creates the item section body for a group
     * @param groupIndex - The group number
     * @returns {HTMLElement} - Returns the item section body element
     */
    private readonly _createItemSectionBody = (groupIndex: number): HTMLElement => {
        const categoryItems = this._createCategoriesAndItemsObject(groupIndex);

        const bodyDiv = document.createElement("div");
        bodyDiv.id = `div_deal_builder_item_body_${groupIndex}`;
        bodyDiv.classList.add("deal-builder-group-body");
        if(categoryItems.length > 1)
            this._buildItemCategoriesSelector(bodyDiv, groupIndex, categoryItems);

        let itemsToRender: IDataItem[] = [];

        const group = this._item.groupState.categoryStates.find(x => x.groupIndex == groupIndex);
        if (group) {
            if (group.selectedCategoryId > -1) {
                itemsToRender = group.categoryItems.find(x => x.id == group.selectedCategoryId)?.items || [];
            } else {
                itemsToRender = group.categoryItems.flatMap(x => x.items);
            }
        }
        this._buildItemSection(bodyDiv, groupIndex, itemsToRender, false);

        return bodyDiv;
    }

    /**
     * Creates an array of data items by category
     * @param groupIndex - The group number
     * @returns {IItemsByCategory[]} - Returns an array of data items by category
     */
    private readonly _createCategoriesAndItemsObject = (groupIndex: number): IItemsByCategory[] => {
        const itemsByCategory: IItemsByCategory[] = [];

        const group = this._item.groupState.categoryStates?.find(x => x.groupIndex == groupIndex);
        if (group) {
            return group.categoryItems;
        }

        if (this._item.coupon) {
            const index = this.isMixAndMatch ? 1 : groupIndex;
            const couponItemIds = this._item.coupon.Items.filter(x => x.GroupIndex == index).map(x => x.ItemId);
            const dataItems = this._data.getProperty("Items")?.filter(x => couponItemIds.some(y => y == x.ItemId));
            const categories = this._data.getProperty("Categories")?.filter(x => dataItems.some(y => y.WebCategoryId == x.ItemWebCategoryId));

            let selectedCategoryId = -1;
            for (const category of categories) {
                if (selectedCategoryId < 0) {
                    selectedCategoryId = category.ItemWebCategoryId;
                }
                const categoryItem: IItemsByCategory = { id: category.ItemWebCategoryId, names: category.Names, items: [] };
                const categoryItems = dataItems.filter(x => x.WebCategoryId == category.ItemWebCategoryId) || [];

                this._processCategoryItems(categoryItems, categoryItem);
                itemsByCategory.push(categoryItem);
            }

            //save the categories and items state for the group
            if (!this._item.groupState.categoryStates?.find(x => x.groupIndex == groupIndex)) {
                this._item.groupState.categoryStates?.push({
                    groupIndex: groupIndex,
                    selectedCategoryId: selectedCategoryId,
                    categoryItems: itemsByCategory
                });
            }
        }

        return itemsByCategory;
    }

    /**
     * Processes the category items and adds them to the category item model
     * @param categoryItems - The data items of a specific category to process
     * @param categoryItem - The category item model to add the items to
     */
    private readonly _processCategoryItems = (categoryItems: IDataItem[], categoryItem: IItemsByCategory): void => {
        for (const item of categoryItems) {
            // Skip items with no valid sizes or QR restrictions
            if (!OnlineOrderingUtil._isItemValid(item, this._item.orderTypeId, !!this._context.getProperty("qrCode")))
                continue;

            categoryItem.items.push(item);
        }
    }

    /**
     * Builds the category buttons bar for a group
     * @param parent - The parent element to append the category buttons bar to
     * @param groupIndex - The group number
     * @param categoryItems - The array of data items by category
     */
    private readonly _buildItemCategoriesSelector = (parent: HTMLElement, groupIndex: number, categoryItems: IItemsByCategory[]): void => {
        const groupDefaultCategoryId = this._getSelectedCategoryIdForGroup(groupIndex);

        const divCategoriesBar = document.createElement("div");
        divCategoriesBar.classList.add("deal-builder-group-body-category");

        const data: IPillBarData = {
            buttonId: "deal_builder_categories_bar",
            buttons: []
        };

        for (const category of categoryItems) {
            const name = this._localizationService.GetName(category.names);
            data.buttons.push(
                {
                    id: `${groupIndex}_${category.id}`,
                    text: name,
                    selected: groupDefaultCategoryId === category.id,
                    ltagj: category.names,
                    function: () => { return this._categoryButtonOnClick(groupIndex, category.id); }
                }
            );
        }

        const pillBar = new PillBar(data, this._localizationService);
        divCategoriesBar.appendChild(pillBar.element);
        parent.appendChild(divCategoriesBar);
    }

    /**
     * Auto selects the category for a group if a default category is set
     * @param groupIndex - The group number
     */
    private readonly _autoSelectItemCategory = (groupIndex: number): void => {
        const groupDefaultCategoryId = this._getSelectedCategoryIdForGroup(groupIndex);
        if (groupDefaultCategoryId > -1) {
            this._categoryButtonOnClick(groupIndex, groupDefaultCategoryId);
        }
    }

    /**
     * Retrieves the selected category ID for a given modifier group index.
     * 
     * @param {number} groupIndex - The index of the modifier group.
     * @returns {number} The selected category ID, or -1 if not found.
     */
    private _getSelectedCategoryIdForGroup(groupIndex: number): number {
        return this._item.groupState.categoryStates.find(x => x.groupIndex === groupIndex)?.selectedCategoryId ?? -1;
    }

    /**
     * Handles the click event for a category button
     * @param groupIndex - The group number the category belongs to
     * @param categoryId - The category ID that was selected
     */
    private readonly _categoryButtonOnClick = (groupIndex: number, categoryId: number): void => {
        const groupItemsSection = document.getElementById(`div_deal_builder_group_items_section_${groupIndex}`);
        if (groupItemsSection) {
            groupItemsSection.classList.add("fade-out");

            groupItemsSection.innerHTML = "";
            this._showItemsOfCategory(groupItemsSection, groupIndex, categoryId);
            groupItemsSection.classList.remove("fade-out");
        }
    }

    /**
     * Builds the item section wrapper for a group
     * @param parent - The parent element to append the item section to
     * @param groupIndex - The group number
     * @param itemsToRender - The data items to render
     * @param showOptions - Whether to show the customize and remove buttons
     * @param itemKey - The order item key if the item was already added to the deal
     */
    private readonly _buildItemSection = (parent: HTMLElement, groupIndex: number, itemsToRender: IDataItem[], showOptions: boolean, itemKey?: number): void => {
        const divItemsContainer = document.createElement("div");
        const categoryItemsSection = this._createItemsSection(groupIndex, itemsToRender, showOptions, itemKey);
        divItemsContainer.appendChild(categoryItemsSection);

        parent.appendChild(divItemsContainer);
    }

    /**
     * Creates the items section for a group
     * @param groupIndex - The group number
     * @param items - The items to render
     * @param showOptions - Whether to show the customize and remove buttons
     * @param itemKey - The order item key if the item was already added to the deal
     * @returns {HTMLElement} - Returns the items section element
     */
    private readonly _createItemsSection = (groupIndex: number, items: IDataItem[], showOptions: boolean, itemKey?: number): HTMLElement => {
        const divItemsSection = document.createElement("div");
        divItemsSection.id = `div_deal_builder_group_items_section_${groupIndex}`;
        divItemsSection.classList.add("deal-builder-group-items-section");
        for (const [index, item] of items.entries()) {
            const itemElement = this._createItemCard(groupIndex, index, item, showOptions, itemKey);
            divItemsSection.appendChild(itemElement);
        }

        if (!showOptions) {
            divItemsSection.setAttribute("role", "radiogroup");
            divItemsSection.setAttribute("aria-labelledby", `h_deal_builder_group_${groupIndex}`);
            this._bindItemRadioGroup(divItemsSection);
            this._resetItemRovingTabindex(divItemsSection);
        }
        return divItemsSection;
    }

    /**
     * Makes the first item radio of a group the single Tab stop
     * @param section - The group items section containing the item radios
     */
    private readonly _resetItemRovingTabindex = (section: HTMLElement): void => {
        const radios = Array.from(section.querySelectorAll<HTMLElement>('[role="radio"]'));
        radios.forEach((radio, index) => { radio.tabIndex = index === 0 ? 0 : -1; });
    }

    /**
     * Binds arrow key navigation and roving tabindex to a group's item radios (bound once per section)
     * @param section - The group items section containing the item radios
     */
    private readonly _bindItemRadioGroup = (section: HTMLElement): void => {
        if (section.dataset.radioNavBound)
            return;
        section.dataset.radioNavBound = "true";

        const getRadios = (): HTMLElement[] => Array.from(section.querySelectorAll<HTMLElement>('[role="radio"]'));

        section.addEventListener("keydown", (e: KeyboardEvent) => {
            const step = ({ ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 } as Record<string, number>)[e.key];
            const radios = getRadios();
            const current = radios.indexOf(e.target as HTMLElement);
            if (!step || current < 0)
                return;

            // Arrows only move focus; selecting opens the customize modal, so that stays on Enter/Space.
            e.preventDefault();
            radios[(current + step + radios.length) % radios.length].focus();
        });

        section.addEventListener("focusin", (e: FocusEvent) => {
            const radios = getRadios();
            if (!radios.includes(e.target as HTMLElement))
                return;
            radios.forEach(radio => { radio.tabIndex = radio === e.target ? 0 : -1; });
        });

        // With nothing checked, tabbing back into the group lands on the first option (AC / APG radio group).
        section.addEventListener("focusout", (e: FocusEvent) => {
            if (!section.contains(e.relatedTarget as Node | null))
                this._resetItemRovingTabindex(section);
        });
    }

    /**
     * Creates an item card for a group
     * @param groupIndex - The group number
     * @param itemIndex - The index representing the item card from teh deal builder list
     * @param item - The item to render
     * @param showOptions - Whether to show the customize and remove buttons
     * @param itemKey - The order item key if the item was already added to the deal
     * @returns {HTMLElement} - Returns the item card element
     */
    private readonly _createItemCard = (groupIndex: number, itemIndex: number, item: IDataItem, showOptions: boolean, itemKey?: number): HTMLElement => {
        const callback = showOptions ? undefined : () => { this._mealDealItemCardOnClick(item, groupIndex); };
        const itemCard = new MealDealItemSectionCard(this._localizationService, item, groupIndex, itemIndex, showOptions, itemKey, callback);
        return itemCard.element;
    }

    /**
     * Handles the click event for an item to be added to the deal
     * @param mItem - The item to add to the deal
     * @param groupIndex - The group number the item belongs to, gets overridden to 1 if Mix and Match
     */
    private readonly _mealDealItemCardOnClick = (mItem: IDataItem, groupIndex: number): void => {
        const sizes = this._getCouponItemSizesByGroup(mItem.ItemId, this.isMixAndMatch ? 1 : groupIndex);
        const [mustCustomize, canCustomize] = this._getCustomizeRules(mItem);
        if (mustCustomize || canCustomize || sizes.length > 1) {
            const defaultSizeId = this._data.getProperty("Settings")?.DSZID;
            const defSize = OnlineOrderingUtil.GetItemDefaultSize(mItem, this._item.orderTypeId, null, defaultSizeId);
            // Start on a coupon-qualified size; the menu default may not qualify (e.g. coupon only allows a hidden size)
            const sizeId = sizes.length === 0 || sizes.includes(defSize.SizeId) ? defSize.SizeId : sizes[0];
            const couponQuantity = this._item.coupon?.Items.find(x => x.GroupIndex == groupIndex && x.ItemId == mItem.ItemId && x.SizeId == sizeId)?.Quantity || 1;
            this._openCustomizeItem(mItem, groupIndex, 0, sizeId, couponQuantity, this._addItemToDeal);
        } else {
            const couponQuantity = this._item.coupon?.Items.find(x => x.GroupIndex == groupIndex && x.ItemId == mItem.ItemId && x.SizeId == sizes[0])?.Quantity || 1;
            this._openCustomizeItem(mItem, groupIndex, 0, sizes[0], couponQuantity, this._addItemToDeal);
        }
    }

    /**
     * Opens the customize item modal for the selected item
     * @param mItem - The item to customize
     * @param groupIndex - The group number the item belongs to
     * @param itemIndex - The index representing the item card from teh deal builder list
     * @param callback - The callback function when the item has completed customization
     * @param orderItems - The existing order items to customize if they were already added to the deal
     */
    private readonly _openCustomizeItem = (mItem: IDataItem, groupIndex: number, itemIndex: number, sizeId: number, couponQuantity: number, callback: Function, orderItems?: IOrderItem[]): void => {
        if (this._dataLayer) {
            // Analytics-only lookup -- an unpriceable item (ADO Bug 86902) must not block opening the customize modal below.
            try {
                const orderItem = this._order._createOrderItem(mItem, sizeId, 0, couponQuantity, []);
                this._dataLayer.select_item(this._order, orderItem, itemIndex, Modals.MEAL_DEAL, this._item.coupon?.CouponId);
            } catch (e) {
                if (!(e instanceof ItemPricingUnavailableError))
                    throw e;
            }
        }

        const group = this._item.groupState.itemStates.find(x => x.groupIndex == groupIndex);
        OnlineOrderingUtil.checkAgeAndCustomizeItem(this._context, this._localizationService, mItem, () => {
            const props: ICustomizeItemProps = {
                mItem: mItem,
                sizeId: sizeId,
                quantity: couponQuantity,
                mealDeal: {
                    coupon: this._item.coupon,
                    selectedGroup: groupIndex,
                    itemKey: group?.itemKey || 0,
                    isMixAndMatch: this.isMixAndMatch,
                    addToDealCallback: callback
                },
                recentOrder: false,
                itemsToEdit: orderItems || []
            };

            this._eventBus.emit(EventTypes.MODAL_OPEN, Modals.CUSTOMIZE_ITEM, props);
        });
    }

    /**
     * Toggle the visibility of the accordion group items section body
     * @param header - The header element to get the next element sibling to toggle visibility
     */
    private readonly _toggleAccordion = (header: HTMLElement): void => {
        const groupItemsSectionBody = header.nextElementSibling as HTMLElement;
        this._setAccordionExpanded(header, groupItemsSectionBody.classList.contains('accordion-closed'));
    }

    /**
     * Expands or collapses a group, keeping the icon, aria-expanded and focusability in sync
     * @param header - The group header element whose next sibling is the group body
     * @param expanded - Whether the group should be expanded
     */
    private readonly _setAccordionExpanded = (header: HTMLElement, expanded: boolean): void => {
        const groupItemsSectionBody = header.nextElementSibling as HTMLElement;
        const icon = header.children[0].querySelector('.accordion-icon') as HTMLElement;

        groupItemsSectionBody.classList.toggle('accordion-closed', !expanded);
        icon.classList.toggle('accordion-closed', !expanded);
        header.querySelector('.deal-builder-group-toggle')?.setAttribute('aria-expanded', String(expanded));
        groupItemsSectionBody.inert = !expanded;
    }

    /**
     * Toggle the content of the group header tag between "Required" and "Added"
     * @param groupIndex - The group number the tag belongs to
     * @param itemIsAdded - Whether the item was added to the deal
     */
    private readonly _toggleGroupHeaderTag = (groupIndex: number, itemIsAdded: boolean): void => {
        const divReqOptionalTag = document.getElementById(`spn_deal_builder_group_header_tag_${groupIndex}`);
        const divAddedTag = document.getElementById(`spn_deal_builder_group_header_tag_added_${groupIndex}`);
        if (divReqOptionalTag && divAddedTag) {
            if (itemIsAdded) {
                divReqOptionalTag.classList.add("hidden");
                divAddedTag.classList.remove("hidden");
            } else {
                divReqOptionalTag.classList.remove("hidden");
                divAddedTag.classList.add("hidden");
            }
        }
    }

    /**
     * Updates the item section when an item is added to the deal
     * @param parentDivId - The parent element ID to append the item section to
     * @param groupIndex - The group number the item belongs to
     * @param itemId - The item ID to add to the deal
     * @param itemKey - The order item key if the item is being added to the deal
     */
    private readonly _updateToAddedItem = (parentDivId: string, groupIndex: number, itemId: number, itemKey?: number): void => {
        const divParent = document.getElementById(parentDivId);
        const group = this._item.groupState.categoryStates.find(x => x.groupIndex == groupIndex);
        if (group) {
            const dataItems = group.categoryItems.flatMap(x => x.items);
            const item = dataItems.find(x => x.ItemId == itemId);
            if (item && divParent) {
                this._buildItemSection(divParent, groupIndex, [item], true, itemKey);
                this._setItemCardEventHandlers();
            }
        }
    }

    /**
     * Sets up event listeners for the customize and remove buttons on the selected item cards
     */
    private readonly _setItemCardEventHandlers = (): void => {
        const customizeBtns = Array.from(document.getElementsByName("btn_meal_deal_customize"));
        for (const button of customizeBtns) {
            const orderItemKey = Number(button.dataset.id);
            const groupId = Number(button.dataset.groupid);
            const itemIdx = Number(button.dataset.index);
            button.onclick = (event: MouseEvent) => {
                this._customizeItemOnClick(event, groupId, itemIdx, orderItemKey);
            }
        }

        const removeBtns = Array.from(document.getElementsByName("btn_meal_deal_remove"));
        for (const button of removeBtns) {
            const id = Number(button.dataset.id);
            const groupId = Number(button.dataset.groupid);
            button.onclick = () => {
                this._removeItemOnClick(groupId, id);
            }
        }
    }

    /**
     * Handles the click event for customizing an item that was already added to the deal
     * @param event - The mouse event containing button info
     * @param groupIndex - The group number the item belongs to
     * @param itemIndex - The index representing the item card from teh deal builder list
     * @param orderItemKey - The order item key of the already added item to customize
     */
    private readonly _customizeItemOnClick = (event: MouseEvent, groupIndex: number, itemIndex: number, orderItemKey: number): void => {
        const groupCategories = this._item.groupState.categoryStates.find(x => x.groupIndex == groupIndex);
        const groupItem = this._item.groupState.itemStates.find(x => x.groupIndex == groupIndex && x.itemKey == orderItemKey);
        if (groupCategories && groupItem) {
            const groupItems = groupCategories.categoryItems.flatMap(x => x.items);
            const mItem = groupItems.find(x => x.ItemId == groupItem.items[0].ItemId);
            if (mItem) {
                this._openCustomizeItem(mItem, groupIndex, itemIndex, groupItem.items[0].SizeId, groupItem.items[0].Quantity, this._addItemToDeal, groupItem.items);

                if (this._dataLayer) {
                    const btnText = (event.target as HTMLButtonElement).innerText ?? '';
                    this._dataLayer.customize_item(btnText, window.location.href, Modals.MEAL_DEAL);
                }
            }
        }
    }

    /**
     * Adds an item to the deal and creates a new group section if coupon is a mix and match type, used as a callback function for the customize item modal
     * @param groupIndex - The group number the item belongs to
     * @param items - The prepared order items to add to the deal
     * @param isEditing - If the items were added or edited from the customize menu
     */
    private readonly _addItemToDeal = (groupIndex: number, items: IOrderItem[], isEditing: boolean): void => {
        const group = this._item.groupState.itemStates.find(x => x.groupIndex == groupIndex);
        if (group && items.length > 0) {
            if (this.isMixAndMatch && !isEditing) {
                const newGroupIndex = this._addNewGroup();
                const divMainBody = document.getElementById("div_deal_builder_main_body");
                if (divMainBody) {                        
                    this._createItemSection(divMainBody, newGroupIndex, this.selectedItemsCount + 1 < this._item.groupState.requiredItems);
                }
            }

            group.items = items;
            group.itemKey = items[0].ItemKey;

            const completedItemsCount = this._item.groupState.itemStates.filter(x => x.itemKey != -1).length;
            this.selectedItemsCount = completedItemsCount;

            this._updateGroupItemSection(groupIndex, items[0].ItemId, true, items[0].ItemKey);
        }
    }

    /**
     * Adds a new group to add items to
     * @returns {number} - Returns the new group index number
     */
    private _addNewGroup(): number {
        this._item.groupState.totalGroups += 1;
        const newGroupIndex = this._item.groupState.totalGroups
        this._item.groupState.itemStates.push({ groupIndex: newGroupIndex, itemKey: -1, items: [], required: false });
        return newGroupIndex;
    }

    /** 
     * Removes a group when an item is removed from the deal
     * @param groupIndex - The group index to remove
     * @returns {IDealBuilderItem | null} - Returns the removed item if the deal is a mix and match, otherwise null
     */
    private _removeGroup(groupIndex: number): IDealBuilderItem | null {
        const requiredItemCount = this._item.groupState.requiredItems;
        let itemToRemove = null;
        if (this.isMixAndMatch){
            const index = this._item.groupState.itemStates.findIndex(x => x.groupIndex == groupIndex);
            if (index !== -1) {
                [itemToRemove] = this._item.groupState.itemStates.splice(index, 1);

                // Renumber remaining items' groupIndex sequentially
                this._item.groupState.itemStates.forEach((item, i) => {
                    item.groupIndex = i + 1;
                    if(i + 1 <= requiredItemCount)
                        item.required = true;
                });
            }
        } else {
            const group = this._item.groupState.itemStates.find(x => x.groupIndex == groupIndex);
            if (group) {
                group.itemKey = -1;
                group.items = [];
            }
        }

        // update groupState
        const completedItemsCount = this._item.groupState.itemStates.filter(x => x.itemKey != -1).length;
        if (this._item.groupState.totalGroups > requiredItemCount && this._item.groupState.totalGroups - 1 > completedItemsCount)
            this._item.groupState.totalGroups -= 1;

        return itemToRemove;
    }

    /**
     * Updates the group item section when an item is added or removed from the deal
     * @param groupIndex - The group number the item belongs to
     * @param itemId - The item ID to add or remove from the deal
     * @param itemIsAdded - Whether the item is being added or removed from the deal
     * @param itemKey - The order item key if the item is being added to the deal
     */
    private readonly _updateGroupItemSection = (groupIndex: number, itemId: number, itemIsAdded: boolean, itemKey?: number): void => {
        //update group item header tag
        this._toggleGroupHeaderTag(groupIndex, itemIsAdded);

        //clear group item section body
        if (itemIsAdded) {
            this._removeItemSectionEventHandlers(groupIndex);
        }
        const parentDivId = `div_deal_builder_item_body_${groupIndex}`;
        Util.setElement("innerHTML", parentDivId, "");

        //update item section
        if (itemIsAdded) {
            this._updateToAddedItem(parentDivId, groupIndex, itemId, itemKey);
        } else {
            //clear group item section header and body and re-render
            const divItemSection = document.getElementById(`div_deal_builder_item_${groupIndex}`);
            const divItemContainer = document.getElementById(parentDivId);
            if (divItemSection && divItemContainer) {
                divItemContainer.remove();
                const divBody = this._createItemSectionBody(groupIndex);
                divItemSection.appendChild(divBody);

                // The rebuilt body is open, so re-sync a header that was left collapsed.
                const header = divItemSection.querySelector<HTMLElement>('[name="deal-builder-group-header"]');
                if (header)
                    this._setAccordionExpanded(header, true);

                this._autoSelectItemCategory(groupIndex);
                this._setEventListeners();
            }
        }

        //update footer button
        this._updateAddToOrderBtn();
    }

    /**
     * Shows the items of a selected category
     * @param parentDiv - The parent element to append the items to
     * @param groupIndex - The group number the items belong to
     * @param categoryId - The category ID to filter the items by
     */
    private readonly _showItemsOfCategory = (parentDiv: HTMLElement, groupIndex: number, categoryId: number): void => {
        const group = this._item.groupState.categoryStates.find(x => x.groupIndex == groupIndex);
        if (group) {
            const filteredItems = group.categoryItems.find(x => x.id == categoryId);
            if (filteredItems) {
                for (const [index, item] of filteredItems.items.entries()) {
                    const itemElement = this._createItemCard(groupIndex, index, item, false);
                    parentDiv.appendChild(itemElement);
                }
                this._resetItemRovingTabindex(parentDiv);
            }
        }
    }

    /**
     * Handles the click event for removing an item from the deal and the order
     * @param groupIndex - The group number the item belongs to
     * @param itemKey - The item Key to remove from the deal and the order
     */
    private readonly _removeItemOnClick = (groupIndex: number, itemKey: number): void => {
        this._removeGroup(groupIndex);

        const completedItemsCount = this._item.groupState.itemStates.filter(x => x.itemKey != -1).length;
        this.selectedItemsCount = completedItemsCount;

        if (this.isMixAndMatch){
            this._renderGroupItemSections();
            this._renderExistingItems();    
        }
        else
            this._updateGroupItemSection(groupIndex, itemKey, false);

        this._order.deleteItemByItemKeyAsync(itemKey, this._loyaltyProvider);
        //} 
    }

    /**
     * Removes the event listeners for the item section, specifically the category buttons and item cards
     * @param groupIndex - The group number the item belongs to
     */
    private readonly _removeItemSectionEventHandlers = (groupIndex: number): void => {
        const divItemsSection = document.getElementById(`div_deal_builder_item_body_${groupIndex}`);
        if (divItemsSection) {
            const itemCards = Array.from(divItemsSection.querySelectorAll("div[name='div_meal_deal_items_item']")) as HTMLElement[];
            for (const itemCard of itemCards) {
                itemCard.onclick = null;
            }
        }
    }

    /**
     * Handles trying to apply the deal/coupon to the cart when the "Add Deal to Order" button is clicked
     */
    private readonly _onClickAddDealToOrder = async (): Promise<void> => {
        await this._tryToAddDealToOrderAsync(true);
        await this._closeDealBuilderAsync();
        await this._order.autoApplyCouponsAsync(this._loyaltyProvider);
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    /**
     * Attempts to add the deal to the order and handles either adding/removing incomplete coupons to/from the order
     * @param showErrorMsg - whether or not the coupon module will display error messages
     */
    private readonly _tryToAddDealToOrderAsync = async (showErrorMsg: boolean): Promise<void> => {
        const result = await this._tryApplyDealAsync(showErrorMsg);
        if (result) {
            await this._order.removeIncompleteCouponByIdAsync(this._loyaltyProvider, this._item.coupon?.CouponId || 0, true);
            const addedToCartMsg = this.selectedItemsCount > 1 ?
                this._localizationService.names("ItemsAddedToCart_Plural").replace("{{number}}", this.selectedItemsCount.toString()) : this._localizationService.names("ItemsAddedToCart_Single");
            const msg = new TimedMessage(addedToCartMsg, this._localizationService.names("ProceedToCartToCheckout"), 1000);
            msg.show();
        } else {
            this._addDealAsIncomplete();
        }
    }

    /**
     * Adds the deal as incomplete to the order
     */
    private readonly _addDealAsIncomplete = (): void => {
        if (!this._item.coupon)
            return;

        const incompleteDiscount: IIncompleteDiscount = {
            couponId: this._item.coupon?.CouponId || 0,
            couponCode: this._item.offerCode,
            channelId: null,
            isCampaignCode: this._item.isCampaignCode,
            isPromotionCode: this._item.isPromotionCode,
            rewardId: this._item.rewardId,
            sourceId: this._item.sourceId,
            isBankedCurrency: this._item.isBankedCurrency,
            isSpecialCode: this._item.isSpecialCode,
            isSingleUseCoupon: this._item.isSingleUseCoupon
        };
        this._order.addIncompleteDiscount(this._loyaltyProvider, incompleteDiscount);
    }

    /**
     * Tries to apply the deal to the cart
     * @param showErrorMsg - whether or not the coupon module will display error messages
     * @returns {boolean | null} - Returns true if the deal was successfully applied, false if not, and null if an error occurred
     */
    private readonly _tryApplyDealAsync = async (showErrorMsg: boolean): Promise<boolean | null> => {
        const params: IApplyCouponByIdParamsPartial = {
            loyaltyProvider: this._loyaltyProvider,
            couponId: this._item.coupon?.CouponId ?? 0,
            couponCode: this._item.offerCode,
            rewardId: this._item.rewardId,
            sourceId: this._item.sourceId,
            isBankedCurrency: this._item.isBankedCurrency,
            isCampaignCode: this._item.isCampaignCode,
            isPromotionCode: this._item.isPromotionCode,
            showErrorMsg: showErrorMsg,
            channelId: this._item.channelId,
            isSUCValidationReq: false,
            isSpecialCode: this._item.isSpecialCode,
            isSingleUseCoupon: this._item.isSingleUseCoupon,
        };
        return await this._order.applyCouponByIdAsync(params);
    }

    /**
     * Checks if the reward is unused and removes it if it is
     */
    private async _checkAndRemoveUnusedRewardAsync(): Promise<void> {
        if (!this._item.rewardId)
            return;

        const reward = this._profileService.user.getRewardInfo(this._item.rewardId);
        if (!reward)
            return;

        const isApplied = this._checkRewardAppliedToOrder(reward);
        const inIncompleteDiscounts = this._order.getIncompleteDiscounts().find(x => x.rewardId === this._item.rewardId);
        if (reward && reward.Used && !isApplied && !inIncompleteDiscounts) {
            await this._loyaltyProvider.removeReward(reward);
            this._eventBus.emit(EventTypes.REWARD_REMOVED, reward.RewardId);
        }
    }

    /**
     * Checks if the reward is currently applied to the order
     * @param reward - The reward to check
     * @returns {boolean} - Returns true if the reward is applied to the order, false otherwise}
     */
    private readonly _checkRewardAppliedToOrder = (reward: IUserLoyaltyReward): boolean => {
        if (!reward) {
            return false;
        }
        const coupon = this._order.getProperty("Coupons").find((x: IOrderCoupon) => x.RewardId === reward.RewardId);
        if (!coupon) {
            return false;
        }
        const itemCoupon = this._order.getProperty("ItemsCoupons").find((x: IOrderItemCoupon) => x.CouponKey === coupon.CouponKey);
        return !!itemCoupon;
    }

    /**
     * Handles the click event for closing the DealBuilder modal
     */
    private _onClickDealBuilderClose = async (): Promise<void> => {
        this._addDealAsIncomplete();
        this._eventBus.emit(EventTypes.CART_CHANGED);
        await this._closeDealBuilderAsync();
    }

    /** 
     * Closes the DealBuilder modal, clears the visual elements, and removes the associated event listeners.
     */
    private readonly _closeDealBuilderAsync = async (): Promise<void> => {
        super.close();
        await this._checkAndRemoveUnusedRewardAsync();
        this._clearDivContent();
        Util.setElement("disabled", "btn_deal_builder_add_to_order", "true");
        document.getElementById("btn_deal_builder_add_to_order")?.removeEventListener("click", this._onClickAddDealToOrder);
        document.getElementById("modal_deal_builder_delete")?.removeEventListener("click", this._onClickDealBuilderClose);
        this._clearModel();
    }

    /**
     * Clears the div content and its height styling
     */
    private _clearDivContent(): void {
        const divContent = document.getElementById("div_deal_builder_content") as HTMLDivElement;
        divContent.innerHTML = "";
        divContent.style.height = "";
        divContent.style.minHeight = "";
    }
}